import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Paper, Stack,
  Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import apiService from '../../services/api';
import { DomainContactCard } from '../../types/domainRegistry';
import { DomainTabProps, TabBody, errorText } from './shared';

type SetName = 'registrant' | 'admin' | 'technical' | 'billing';
const SETS: { key: SetName; cacheKey: 'registrant' | 'admin' | 'tech' | 'billing'; label: string }[] = [
  { key: 'registrant', cacheKey: 'registrant', label: 'Registrant' },
  { key: 'admin', cacheKey: 'admin', label: 'Admin' },
  { key: 'technical', cacheKey: 'tech', label: 'Technical' },
  { key: 'billing', cacheKey: 'billing', label: 'Billing' },
];

interface Form { organisation: string; firstname: string; lastname: string; address: string; suburb: string; state: string; country: string; postcode: string; phone: string; fax: string; email: string }
const blank = (): Form => ({ organisation: '', firstname: '', lastname: '', address: '', suburb: '', state: '', country: 'AU', postcode: '', phone: '', fax: '', email: '' });
const fromCard = (c?: DomainContactCard): Form => ({
  organisation: c?.company || '', firstname: c?.firstname || '', lastname: c?.lastname || '',
  address: [c?.address1, c?.address2, c?.address3].filter(Boolean).join('\n'), suburb: c?.suburb || '', state: c?.state || '',
  country: c?.country || 'AU', postcode: c?.postcode || '', phone: c?.phone || '', fax: c?.fax || '', email: c?.email || '',
});
const toPayload = (f: Form) => ({ ...f, address: f.address.split('\n').map((x) => x.trim()).filter(Boolean) });

const FIELDS: { key: keyof Form; label: string; xs: number; multiline?: boolean }[] = [
  { key: 'organisation', label: 'Organisation', xs: 12 }, { key: 'firstname', label: 'First name', xs: 6 }, { key: 'lastname', label: 'Last name', xs: 6 },
  { key: 'address', label: 'Address (one line per row)', xs: 12, multiline: true }, { key: 'suburb', label: 'Suburb', xs: 6 }, { key: 'state', label: 'State', xs: 3 },
  { key: 'postcode', label: 'Postcode', xs: 3 }, { key: 'country', label: 'Country (2 letters)', xs: 4 }, { key: 'phone', label: 'Phone (e.g. +61.412345678)', xs: 4 },
  { key: 'fax', label: 'Fax', xs: 4 }, { key: 'email', label: 'Email', xs: 12 },
];

const ContactCardView: React.FC<{ title: string; c?: DomainContactCard }> = ({ title, c }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 0 }}>
    <Typography variant="subtitle2" gutterBottom>{title}</Typography>
    {!c || !(c.firstname || c.lastname || c.email) ? (
      <Typography variant="body2" color="text.secondary">Not available</Typography>
    ) : (
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={600}>{[c.firstname, c.lastname].filter(Boolean).join(' ')}</Typography>
        {c.company && <Typography variant="body2">{c.company}</Typography>}
        <Typography variant="body2">{[c.address1, c.address2, c.address3].filter(Boolean).join(', ')}</Typography>
        <Typography variant="body2">{[c.suburb, c.state, c.postcode, c.country].filter(Boolean).join(' ')}</Typography>
        {c.phone && <Typography variant="body2">{c.phone}</Typography>}
        {c.email && <Typography variant="body2">{c.email}</Typography>}
      </Stack>
    )}
  </Paper>
);

const ContactsTab: React.FC<DomainTabProps> = ({ serviceId, info, onChanged }) => {
  const idProtect = (info?.id_protect || '').toLowerCase() === 'enabled';
  const contacts = info?.contacts || {};
  const [open, setOpen] = useState(false);
  const [forms, setForms] = useState<Record<SetName, Form>>({ registrant: blank(), admin: blank(), technical: blank(), billing: blank() });
  const [same, setSame] = useState(true);
  const [active, setActive] = useState<SetName>('registrant');
  const [toggleProtect, setToggleProtect] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  const openEdit = () => {
    setForms({
      registrant: fromCard(contacts.registrant), admin: fromCard(contacts.admin), technical: fromCard(contacts.tech), billing: fromCard(contacts.billing),
    });
    setSame(true);
    setActive('registrant');
    setToggleProtect(true);
    setDialogError(null);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setDialogError(null);
    try {
      await apiService.domainRequest('put', serviceId, '/contacts', {
        registrant: toPayload(forms.registrant),
        ...(same ? {} : { admin: toPayload(forms.admin), technical: toPayload(forms.technical), billing: toPayload(forms.billing) }),
        temporarily_disable_id_protection: toggleProtect,
      });
      setOpen(false);
      setMsg({ severity: 'success', text: 'Contacts updated. For some domains the registrant may be emailed to confirm the change.' });
      await onChanged();
    } catch (e) {
      setDialogError(errorText(e, 'Could not update the contacts.'));
    } finally {
      setSaving(false);
    }
  };

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    setMsg(null);
    try {
      await fn();
      setMsg({ severity: 'success', text: ok });
      await onChanged();
    } catch (e) {
      setMsg({ severity: 'error', text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  const setField = (set: SetName, key: keyof Form, value: string) => setForms((f) => ({ ...f, [set]: { ...f[set], [key]: value } }));
  const visible: SetName[] = same ? ['registrant'] : ['registrant', 'admin', 'technical', 'billing'];

  return (
    <TabBody>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={idProtect}
                disabled={busy === 'protect'}
                onChange={(e) => run('protect', () => apiService.domainRequest('post', serviceId, '/id-protection', { enabled: e.target.checked }), `ID protection turned ${e.target.checked ? 'on' : 'off'}.`)}
              />
            }
            label="ID protection (hides the contact details in WHOIS)"
          />
          {idProtect && <Typography variant="caption" color="text.secondary" display="block">The contact details below are Synergy's protected ones; the real details are used when you edit.</Typography>}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" disabled={busy === 'resend'} onClick={() => run('resend', () => apiService.domainRequest('post', serviceId, '/resend-registrant-emails'), 'Confirmation emails resent.')}>Resend confirmation emails</Button>
          <Button variant="contained" onClick={openEdit}>Edit contacts</Button>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        {SETS.map((s) => (
          <Grid item xs={12} md={6} key={s.key}><ContactCardView title={s.label} c={contacts[s.cacheKey]} /></Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={saving ? undefined : () => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit contacts</DialogTitle>
        <DialogContent dividers>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <FormControlLabel control={<Checkbox checked={same} onChange={(e) => setSame(e.target.checked)} />} label="Use these details for the admin, technical and billing contacts too" />
          {idProtect && (
            <FormControlLabel
              control={<Checkbox checked={toggleProtect} onChange={(e) => setToggleProtect(e.target.checked)} />}
              label="Switch ID protection off while saving, then back on (required by the registry)"
            />
          )}
          {!same && (
            <Tabs value={active} onChange={(_, v) => setActive(v)} sx={{ mb: 2 }}>
              {visible.map((k) => <Tab key={k} value={k} label={SETS.find((s) => s.key === k)!.label} />)}
            </Tabs>
          )}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {FIELDS.map((f) => (
              <Grid item xs={12} sm={f.xs} key={f.key}>
                <TextField
                  size="small"
                  fullWidth
                  label={f.label}
                  value={forms[same ? 'registrant' : active][f.key]}
                  onChange={(e) => setField(same ? 'registrant' : active, f.key, e.target.value)}
                  multiline={f.multiline}
                  minRows={f.multiline ? 2 : undefined}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Save contacts'}</Button>
        </DialogActions>
      </Dialog>
    </TabBody>
  );
};

export default ContactsTab;
